import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SortingRule } from "react-table";
import styled from "styled-components";
import useSWR from "swr";
import { DropDown } from "../../../../components/DropDown/DropDown";
import { DropdownContainer } from "../../../../components/Layout/Layout";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { createChangePerPageHandler } from "../../../../components/Pagination/util";
import {
  getProductStatusColor,
  getProductStatusText,
  StatusLeft,
} from "../../../../components/Status/Status";
import { Table } from "../../../../components/Table/Table";
import { ContentWrapper } from "../../../../layout/portalPageLayout";
import type { WithPagination } from "../../../../types/types";
import type {
  PIMProductActivity,
  ProductStatusType,
} from "../../../../types/types.PIM";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  getDateTime,
  useStoreState,
} from "../../../../util/util";
import type { Getter } from "@tanstack/react-table";
import { EMPTY_VALUE } from "./utils";
import { ActivityHistoryItem } from "./types";
import {
  getActivityListEndpoint,
  getActivityListParams,
} from "../../../../util/activityEndpoints";
import type { ActivityTargetType } from "../../../../util/activityEndpoints";
import { spacingTokens } from "../../../../designTokens";
import { PROFILE_DETAIL_CLASSES } from "../../../../layout/shared/ProfileDetailLayout";

type ModifiedType = {
  time: string;
  date: string;
};

type ActivityTableData = {
  id: string;
  version_number: string;
  status: string[];
  target_type: string[];
  target_id: string;
  last_modified_by: string[];
  modified_at: ModifiedType[];
  actions: { restore: boolean; view: boolean }[];
};

const RowBox = styled.div`
  height: 52px;
  display: flex;
  justify-content: start;
  align-items: center;
`;

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  position: relative;
  z-index: 1;
`;

function transformData(data: PIMProductActivity[]) {
  const grouped = data.reduce<Record<number, ActivityTableData>>(
    (
      acc,
      {
        target_id,
        modified_at,
        last_modified_by,
        id,
        target_type,
        version_number,
        status,
      }
    ) => {
      const modTime = {
        date: getDateTime(modified_at).date,
        time: getDateTime(modified_at).time,
      };
      if (acc[version_number]) {
        acc[version_number].status.push(status);
        acc[version_number].target_type.push(target_type ?? EMPTY_VALUE);
        acc[version_number].last_modified_by.push(last_modified_by);
        acc[version_number].modified_at.push(modTime);
        acc[version_number].actions.push({ restore: true, view: true });
      } else {
        acc[version_number] = {
          id,
          version_number: version_number.toString(),
          status: [status],
          target_type: [target_type ?? EMPTY_VALUE],
          target_id,
          last_modified_by: [last_modified_by],
          modified_at: [modTime],
          actions: [{ restore: true, view: true }],
        };
      }
      return acc;
    },
    {}
  );

  // Sort each version's modified_at array desc
  Object.values(grouped).forEach((version) => {
    version.modified_at.sort(
      (a, b) =>
        new Date(`${b.date} ${b.time}`).getTime() -
        new Date(`${a.date} ${a.time}`).getTime()
    );
  });

  // Sort versions descending (3, 2, 1, 0) by latest modified_at
  return Object.values(grouped).sort((a, b) => {
    const aLatest = new Date(
      `${a.modified_at[0].date} ${a.modified_at[0].time}`
    ).getTime();
    const bLatest = new Date(
      `${b.modified_at[0].date} ${b.modified_at[0].time}`
    ).getTime();
    return bLatest - aLatest;
  });
}

export function Activity({
  target_id,
  hiddenColumns,
  mode,
  activityData,
}: {
  target_id: string;
  hiddenColumns?: string[];
  mode: ActivityTargetType;
  activityData?: ActivityHistoryItem[];
}) {
  const perPageItems = [10, 20, 50];
  const [tableData, setTableData] = useState<ActivityTableData[]>([]);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [offset, setOffset] = useState(0);
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    sortBy: "modified_at",
    orderBy: "desc",
  });
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const shouldFetch = !activityData || activityData.length === 0;

  const activityEndpoint = useMemo(
    () => getActivityListEndpoint(mode, tenant_id, target_id),
    [mode, target_id, tenant_id]
  );

  const swrParams = useMemo(
    () => ({
      params: getActivityListParams(mode, target_id, {
        limit: perPage,
        offset,
        order_by: sortingRules.orderBy || "desc",
        sort_by: sortingRules.sortBy || "modified_at",
      }),
    }),
    [
      mode,
      offset,
      perPage,
      sortingRules.orderBy,
      sortingRules.sortBy,
      target_id,
    ]
  );

  const { data: productActivity, error: productActivityError } = useSWR<
    WithPagination<{ data: PIMProductActivity[] }>
  >(shouldFetch ? [activityEndpoint, swrParams] : null);

  const isLoading = shouldFetch
    ? !productActivity && !productActivityError
    : false;

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const tableColumns = React.useMemo(
    () => [
      {
        header: t("Version"),
        accessorKey: "version_number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <RowBox>{getValue()}</RowBox>
        ),
      },
      {
        header: t("Last Modified"),
        accessorKey: "modified_at",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<ModifiedType[]> }) => {
          return getValue().map(({ date, time }, idx) => (
            <RowBox
              key={`${date}-${time}-${idx}`}
              style={{ gap: spacingTokens.spacing_xl }}
            >
              <div>{date}</div>
              <div>{time}</div>
            </RowBox>
          ));
        },
      },
      {
        header: t("Modified"),
        accessorKey: "target_type",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string[]> }) => {
          return getValue().map((target_type, idx) => (
            <RowBox key={target_type + idx}>{target_type}</RowBox>
          ));
        },
      },
      {
        header: t("Last Modified By"),
        accessorKey: "last_modified_by",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string[]> }) => {
          return getValue().map((last_modified_by, index) => (
            <RowBox key={last_modified_by + index}>{last_modified_by}</RowBox>
          ));
        },
      },
      {
        header: t("Status"),
        accessorKey: "status",
        enableSorting: false,
        align: "left",
        cell: ({ getValue }: { getValue: Getter<ProductStatusType[]> }) => {
          return getValue().map((status, index) => {
            const colorKey = getProductStatusColor(status);
            const textKey = getProductStatusText(status, t);
            return (
              <RowBox key={status + index}>
                <StatusLeft color={colorKey} text={""} />
                <span>{textKey}</span>
              </RowBox>
            );
          });
        },
      },
    ],
    [t]
  );

  useEffect(() => {
    const handleData = ({
      data,
      pagination,
    }: WithPagination<{ data: PIMProductActivity[] }>) => {
      const activities = transformData(data);
      setTableData(activities);
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };
    if (!shouldFetch && activityData && activityData.length) {
      const paginatedData = activityData.slice(offset, offset + perPage);
      const activities = transformData(paginatedData as PIMProductActivity[]);
      setTableData(activities);
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(activityData.length / perPage),
        pageIndex: Math.floor(offset / perPage) + 1,
      });
    } else if (productActivity) {
      handleData(productActivity);
    }
  }, [perPage, productActivity, activityData, shouldFetch, offset]);

  return (
    <ContentWrapper className={PROFILE_DETAIL_CLASSES.activity}>
      <ControlsWrapper>
        <DropdownContainer>
          <DropDown
            items={perPageItems}
            activeItem={perPage}
            textLeft={t("items") + ":"}
            textRight={t(" Per Page")}
            direction={"right"}
            className={"per_Page"}
            clickHandler={changePerPage}
          />
        </DropdownContainer>
      </ControlsWrapper>
      <Table
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={shouldFetch ? productActivityError : undefined}
        handleSort={handleSort}
        hiddenColumns={hiddenColumns}
        lastChildleftAlign
      />
      <Pagination
        pagination={tablePagination}
        offset={offset}
        handlePageClick={changePage}
      />
    </ContentWrapper>
  );
}
